import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import InfoCard from "../../components/card/infocard";
import {
  DeleteItem,
  InviteModal,
  SuccessfulDelete,
  SuccessModal,
} from "../../components/modal";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import DeleteImg from "../../assets/delLogo.png";
import verifyImg from "../../assets/verifyImg.png";
import {
  deleteHoliday,
  getHolidayById,
  notifyHoliday,
  updateHoliday,
} from "../../api/holidays";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";
import {
  fromDateTimeLocalValue,
  toDateTimeLocalValue,
} from "../../utils/dateTimeLocal";

const HolidayDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const holidayId = useMemo(
    () =>
      location.state?.holidayId ||
      sessionStorage.getItem("selectedHolidayId"),
    [location.state],
  );
  const cacheKey = useMemo(
    () => `cehr_holiday_details_${holidayId || "unknown"}`,
    [holidayId],
  );
  const cachedData = useMemo(
    () => readSessionCache(cacheKey, { holiday: null }),
    [cacheKey],
  );

  const [holiday, setHoliday] = useState(cachedData?.holiday || null);
  const [isLoading, setIsLoading] = useState(!cachedData?.holiday);
  const [errorMessage, setErrorMessage] = useState("");
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openNotifyModal, setOpenNotifyModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openNotifySuccessModal, setOpenNotifySuccessModal] = useState(false);
  const [openDeleteSuccessModal, setOpenDeleteSuccessModal] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [notifyErrorMessage, setNotifyErrorMessage] = useState("");
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isNotifying, setIsNotifying] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [holidayName, setHolidayName] = useState("");
  const [holidayDescription, setHolidayDescription] = useState("");
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [category, setCategory] = useState("Company");
  const [status, setStatus] = useState("Active");
  const [country, setCountry] = useState("");
  const [flagEmoji, setFlagEmoji] = useState("");

  const [notifyTitle, setNotifyTitle] = useState("");
  const [notifyMessage, setNotifyMessage] = useState("");
  const [notifyAt, setNotifyAt] = useState("");

  const loadHoliday = useCallback(
    async ({ silent = false } = {}) => {
      if (!holidayId) {
        setErrorMessage("Select a holiday from the list first.");
        return;
      }

      try {
        if (!silent) {
          setIsLoading(true);
        }
        setErrorMessage("");

        const response = await getHolidayById(holidayId);
        const loadedHoliday = response?.holiday || null;

        setHoliday(loadedHoliday);
        setHolidayName(loadedHoliday?.holidayName || "");
        setHolidayDescription(loadedHoliday?.holidayDescription || "");
        setStartAt(toDateTimeLocalValue(loadedHoliday?.startAt));
        setEndAt(toDateTimeLocalValue(loadedHoliday?.endAt));
        setCategory(loadedHoliday?.category || "Company");
        setStatus(loadedHoliday?.status || "Active");
        setCountry(loadedHoliday?.country || "");
        setFlagEmoji(loadedHoliday?.flagEmoji || "");
        writeSessionCache(cacheKey, { holiday: loadedHoliday });
      } catch (error) {
        const responseMessage =
          error?.message || "Unable to load holiday details. Please try again.";
        setErrorMessage(responseMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [cacheKey, holidayId],
  );

  useEffect(() => {
    loadHoliday({ silent: true });
  }, [loadHoliday]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadHoliday({ silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadHoliday]);

  const handleUpdateHoliday = async (ev) => {
    ev.preventDefault();

    if (!holidayId) {
      setFormErrorMessage("Unable to update this holiday right now.");
      return;
    }

    if (!holidayName.trim() || !startAt) {
      setFormErrorMessage("Holiday name and date are required.");
      return;
    }

    try {
      setIsSaving(true);
      setFormErrorMessage("");

      await updateHoliday(holidayId, {
        holidayName: holidayName.trim(),
        holidayDescription: holidayDescription.trim() || undefined,
        category,
        status,
        startAt: fromDateTimeLocalValue(startAt),
        endAt: fromDateTimeLocalValue(endAt),
        country: country.trim() || undefined,
        flagEmoji: flagEmoji.trim() || undefined,
      });

      await loadHoliday({ silent: true });
      setOpenEditModal(false);
      setOpenSuccessModal(true);
    } catch (error) {
      const responseMessage =
        error?.message || "Unable to update holiday. Please try again.";
      setFormErrorMessage(responseMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleNotifyHoliday = async (ev) => {
    ev.preventDefault();

    if (!holidayId) {
      setNotifyErrorMessage("Unable to notify staff right now.");
      return;
    }

    try {
      setIsNotifying(true);
      setNotifyErrorMessage("");

      await notifyHoliday(holidayId, {
        title: notifyTitle.trim() || undefined,
        message: notifyMessage.trim() || undefined,
        scheduledFor: fromDateTimeLocalValue(notifyAt),
      });

      setOpenNotifyModal(false);
      setOpenNotifySuccessModal(true);
      setNotifyTitle("");
      setNotifyMessage("");
      setNotifyAt("");
    } catch (error) {
      const responseMessage =
        error?.message || "Unable to notify staff. Please try again.";
      setNotifyErrorMessage(responseMessage);
    } finally {
      setIsNotifying(false);
    }
  };

  const handleDeleteHoliday = async () => {
    if (!holidayId) {
      setDeleteErrorMessage("Unable to delete this holiday right now.");
      return;
    }

    try {
      setIsDeleting(true);
      setDeleteErrorMessage("");

      await deleteHoliday(holidayId);

      setOpenDeleteModal(false);
      setOpenDeleteSuccessModal(true);
      sessionStorage.removeItem("selectedHolidayId");

      setTimeout(() => {
        navigate("/company-settings/holidays");
      }, 1200);
    } catch (error) {
      const responseMessage =
        error?.message || "Unable to delete holiday. Please try again.";
      setDeleteErrorMessage(responseMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const title = holiday?.holidayName || "Holiday";
  const descriptionText =
    holiday?.holidayDescription || "No holiday description available yet.";
  const createdAt = holiday?.createdAt
    ? new Date(holiday.createdAt).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "-";
  const startAtLabel = holiday?.startAt
    ? new Date(holiday.startAt).toLocaleString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

  return (
    <div>
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Holidays", path: "/company-settings/holidays" },
          { label: title },
        ]}
        title={title}
        subtitle="Holiday details and management actions."
        fallbackPath="/company-settings/holidays"
        backLabel="Back to Holidays"
        actions={
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setFormErrorMessage("");
                setOpenEditModal(true);
              }}
              className="border border-[#1234CD] text-[#1234CD] px-4 py-2 rounded-xl"
            >
              Edit
            </button>
            <button
              onClick={() => {
                setNotifyErrorMessage("");
                setOpenNotifyModal(true);
              }}
              className="border border-[#0F766E] text-[#0F766E] px-4 py-2 rounded-xl"
            >
              Notify
            </button>
            <button
              onClick={() => setOpenDeleteModal(true)}
              className="border border-red-500 text-red-600 px-4 py-2 rounded-xl"
            >
              Delete
            </button>
          </div>
        }
      />

      {isLoading && !holiday ? (
        <FetchLoadingState
          message="Loading holiday details..."
          className="min-h-[60vh]"
        />
      ) : null}

      {errorMessage && !holiday ? (
        <div
          className="px-6 py-4 text-sm text-red-600"
          role="alert"
        >
          {errorMessage}
        </div>
      ) : null}

      {!isLoading && holiday ? (
        <InfoCard
          details="Holiday Details"
          title={title}
          description={descriptionText}
          metaData={[
            { label: "Holiday date", value: startAtLabel },
            { label: "Category", value: holiday.category || "-" },
            { label: "Status", value: holiday.status || "-" },
            { label: "Country", value: holiday.country || "-" },
            { label: "Created", value: createdAt },
          ]}
        />
      ) : null}

      <InviteModal
        isOpen={openEditModal}
        onClose={() => setOpenEditModal(false)}
      >
        <div className="my-8">
          <div>
            <p className="text-2xl font-semibold text-black font-Nunito">
              Edit Holiday
            </p>
          </div>
          <form
            className="my-8"
            onSubmit={handleUpdateHoliday}
          >
            {formErrorMessage && (
              <p
                className="mb-4 text-sm text-red-600"
                role="alert"
              >
                {formErrorMessage}
              </p>
            )}

            <div>
              <label
                htmlFor="edit-holiday-name"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
              >
                Holiday Name
              </label>
              <input
                type="text"
                id="edit-holiday-name"
                placeholder="Holiday name"
                value={holidayName}
                onChange={(ev) => setHolidayName(ev.target.value)}
                className="w-full p-2 text-center mb-3 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div>
              <label
                htmlFor="edit-holiday-description"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
              >
                Description
              </label>
              <textarea
                id="edit-holiday-description"
                placeholder="Optional holiday description"
                value={holidayDescription}
                onChange={(ev) => setHolidayDescription(ev.target.value)}
                className="w-full p-2 min-h-24 mb-3 text-center rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="edit-holiday-start"
                  className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
                >
                  Start Date & Time
                </label>
                <input
                  type="datetime-local"
                  id="edit-holiday-start"
                  value={startAt}
                  onChange={(ev) => setStartAt(ev.target.value)}
                  className="w-full p-2 text-center mb-3 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
                />
              </div>

              <div>
                <label
                  htmlFor="edit-holiday-end"
                  className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
                >
                  End Date & Time (optional)
                </label>
                <input
                  type="datetime-local"
                  id="edit-holiday-end"
                  value={endAt}
                  onChange={(ev) => setEndAt(ev.target.value)}
                  className="w-full p-2 text-center mb-3 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="edit-holiday-category"
                  className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
                >
                  Category
                </label>
                <select
                  id="edit-holiday-category"
                  value={category}
                  onChange={(ev) => setCategory(ev.target.value)}
                  className="w-full p-2 text-center mb-3 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
                >
                  <option value="Company">Company</option>
                  <option value="Public">Public</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="edit-holiday-status"
                  className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
                >
                  Status
                </label>
                <select
                  id="edit-holiday-status"
                  value={status}
                  onChange={(ev) => setStatus(ev.target.value)}
                  className="w-full p-2 text-center mb-3 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="edit-holiday-country"
                  className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
                >
                  Country (optional)
                </label>
                <input
                  type="text"
                  id="edit-holiday-country"
                  placeholder="Country"
                  value={country}
                  onChange={(ev) => setCountry(ev.target.value)}
                  className="w-full p-2 text-center mb-3 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
                />
              </div>

              <div>
                <label
                  htmlFor="edit-holiday-flag"
                  className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
                >
                  Flag Emoji (optional)
                </label>
                <input
                  type="text"
                  id="edit-holiday-flag"
                  placeholder="e.g. 🇳🇬"
                  value={flagEmoji}
                  onChange={(ev) => setFlagEmoji(ev.target.value)}
                  className="w-full p-2 text-center mb-3 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSaving}
              className="w-full bg-[#1234CD] text-white py-3 rounded-xl mt-4 disabled:opacity-60"
            >
              {isSaving ? "Saving..." : "Save changes"}
            </button>
          </form>
        </div>
      </InviteModal>

      <InviteModal
        isOpen={openNotifyModal}
        onClose={() => setOpenNotifyModal(false)}
      >
        <div className="my-8">
          <div>
            <p className="text-2xl font-semibold text-black font-Nunito">
              Notify Staff
            </p>
          </div>
          <form
            className="my-8"
            onSubmit={handleNotifyHoliday}
          >
            {notifyErrorMessage && (
              <p
                className="mb-4 text-sm text-red-600"
                role="alert"
              >
                {notifyErrorMessage}
              </p>
            )}

            <div>
              <label
                htmlFor="notify-title"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
              >
                Notification Title (optional)
              </label>
              <input
                type="text"
                id="notify-title"
                placeholder="Holiday notice title"
                value={notifyTitle}
                onChange={(ev) => setNotifyTitle(ev.target.value)}
                className="w-full p-2 text-center mb-3 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div>
              <label
                htmlFor="notify-message"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
              >
                Notification Message (optional)
              </label>
              <textarea
                id="notify-message"
                placeholder="Holiday message"
                value={notifyMessage}
                onChange={(ev) => setNotifyMessage(ev.target.value)}
                className="w-full p-2 min-h-24 mb-3 text-center rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div>
              <label
                htmlFor="notify-schedule"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
              >
                Schedule notification (optional)
              </label>
              <input
                type="datetime-local"
                id="notify-schedule"
                value={notifyAt}
                onChange={(ev) => setNotifyAt(ev.target.value)}
                className="w-full p-2 text-center mb-3 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <button
              type="submit"
              disabled={isNotifying}
              className="w-full bg-[#0F766E] text-white py-3 rounded-xl mt-4 disabled:opacity-60"
            >
              {isNotifying ? "Sending..." : "Send notification"}
            </button>
          </form>
        </div>
      </InviteModal>

      <DeleteItem
        isOpen={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
      >
        <div className="flex justify-center items-center">
          <img
            src={DeleteImg}
            alt="delete"
          />
        </div>
        <div className="my-8 flex flex-col items-center">
          <h4 className="text-lg font-medium text-black">Delete holiday</h4>
          <p className="text-sm text-gray-500 text-center mt-2">
            Are you sure you want to delete this holiday?
          </p>
          {deleteErrorMessage && (
            <p className="text-xs text-red-600 mt-2">{deleteErrorMessage}</p>
          )}
        </div>
        <div className="flex justify-center gap-3 mt-4">
          <button
            onClick={() => setOpenDeleteModal(false)}
            className="px-4 py-2 rounded-xl border border-gray-300 text-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteHoliday}
            disabled={isDeleting}
            className="px-4 py-2 rounded-xl bg-red-600 text-white disabled:opacity-60"
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </DeleteItem>

      <SuccessModal
        isOpen={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
      >
        <div className="text-center">
          <img
            className="w-24 h-24 mx-auto"
            src={verifyImg}
            alt="success"
          />
          <h2 className="text-black text-center text-3xl leading-[27px] font-bold mb-2">
            Holiday updated
          </h2>
          <p className="text-black text-center text-lg leading-[18.9px]">
            Holiday details were updated successfully.
          </p>
        </div>
      </SuccessModal>

      <SuccessModal
        isOpen={openNotifySuccessModal}
        onClose={() => setOpenNotifySuccessModal(false)}
      >
        <div className="text-center">
          <img
            className="w-24 h-24 mx-auto"
            src={verifyImg}
            alt="success"
          />
          <h2 className="text-black text-center text-3xl leading-[27px] font-bold mb-2">
            Notification queued
          </h2>
          <p className="text-black text-center text-lg leading-[18.9px]">
            Staff will be notified about this holiday.
          </p>
        </div>
      </SuccessModal>

      <SuccessfulDelete
        isOpen={openDeleteSuccessModal}
        onClose={() => setOpenDeleteSuccessModal(false)}
      >
        <div className="flex justify-center items-center">
          <img
            className="w-16 h-16"
            src={verifyImg}
            alt="success"
          />
        </div>
        <p className="text-center text-base font-medium text-black mt-3">
          Holiday deleted
        </p>
      </SuccessfulDelete>
    </div>
  );
};

export default HolidayDetails;
